import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Wrapper = styled.div`
  position: relative;
  line-height: 0;
  overflow: hidden;
  width: 100%;
`;

const Canvas = styled.canvas`
  display: block;
  width: 100%;
  cursor: crosshair;
`;

const DeleteButton = styled.button`
  position: absolute;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: none;
  background: #dc3545;
  color: white;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  line-height: 1;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

  &:hover {
    background: #c82333;
  }
`;

const MIN_RECT_SIZE = 2;
const DEBOUNCE_DELAY = 150;

let nextRectId = 1;

const ImageRedactor = ({ src, initialRects, onChange, onRedactedImageChange, previewMode, className }) => {
  const { t } = useTranslation(['app', 'shared']);
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const imgRef = useRef(null);
  const drawingRef = useRef(false);
  const startRef = useRef({ x: 0, y: 0 });
  const currentRef = useRef(null);
  const debounceTimerRef = useRef(null);

  const [rects, setRects] = useState(initialRects);
  const [scale, setScale] = useState(1);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Store callbacks in refs to avoid useEffect dependency issues
  const onRedactedImageChangeRef = useRef(onRedactedImageChange);
  onRedactedImageChangeRef.current = onRedactedImageChange;

  const updateScale = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || canvas.width === 0) return;
    const displayWidth = canvas.getBoundingClientRect().width;
    setScale(displayWidth / canvas.width);
  }, []);

  const redraw = useCallback(
    extraRect => {
      const canvas = canvasRef.current;
      const img = imgRef.current;
      if (!canvas || !img) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      rects.forEach(r => {
        ctx.fillStyle = previewMode ? '#000' : 'rgba(0,0,0,0.55)';
        ctx.fillRect(r.x, r.y, r.w, r.h);
      });

      if (extraRect) {
        ctx.fillStyle = 'rgba(0,0,0,0.55)';
        ctx.fillRect(extraRect.x, extraRect.y, extraRect.w, extraRect.h);
      }
    },
    [rects, previewMode]
  );

  const emitRedactedImage = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !onRedactedImageChangeRef.current) return;

    // Debounce the base64 generation for performance
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      try {
        // Use an offscreen canvas to generate base64 with solid black
        // This keeps the visible canvas unchanged
        const offscreen = document.createElement('canvas');
        offscreen.width = canvas.width;
        offscreen.height = canvas.height;
        const ctx = offscreen.getContext('2d');
        if (!ctx) return;
        ctx.drawImage(img, 0, 0);
        rects.forEach(r => {
          ctx.fillStyle = '#000';
          ctx.fillRect(r.x, r.y, r.w, r.h);
        });
        const base64 = offscreen.toDataURL('image/jpeg', 0.92);
        onRedactedImageChangeRef.current(base64);
      } catch {
        // Canvas toDataURL can fail with quota exceeded errors
      }
    }, DEBOUNCE_DELAY);
  }, [rects]);

  const getCanvasPos = useCallback(e => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top) * (canvas.height / rect.height),
    };
  }, []);

  const handleMouseDown = useCallback(
    e => {
      const pos = getCanvasPos(e);
      startRef.current = pos;
      drawingRef.current = true;
      currentRef.current = { x: pos.x, y: pos.y, w: 0, h: 0 };
    },
    [getCanvasPos]
  );

  const handleMouseMove = useCallback(
    e => {
      if (!drawingRef.current) return;
      const pos = getCanvasPos(e);
      currentRef.current = {
        x: Math.min(startRef.current.x, pos.x),
        y: Math.min(startRef.current.y, pos.y),
        w: Math.abs(pos.x - startRef.current.x),
        h: Math.abs(pos.y - startRef.current.y),
      };
      redraw(currentRef.current);
    },
    [getCanvasPos, redraw]
  );

  const commitRect = useCallback(() => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const r = currentRef.current;
    currentRef.current = null;

    if (r && r.w > MIN_RECT_SIZE && r.h > MIN_RECT_SIZE) {
      const next = [...rects, { ...r, id: nextRectId++ }];
      setRects(next);
      onChange?.(next);
    } else {
      redraw();
    }
  }, [rects, onChange, redraw]);

  const removeRect = useCallback(
    index => {
      const next = rects.filter((_, i) => i !== index);
      setRects(next);
      onChange?.(next);
    },
    [rects, onChange]
  );

  const handleDeleteClick = useCallback(
    e => {
      const index = parseInt(e.currentTarget.dataset.index, 10);
      removeRect(index);
    },
    [removeRect]
  );

  const stopPropagation = useCallback(e => {
    e.stopPropagation();
  }, []);

  // Load image and initialize canvas
  useEffect(() => {
    setImageLoaded(false);
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      imgRef.current = img;
      setImageError(false);
      setImageLoaded(true);
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      updateScale();
    };

    img.onerror = () => {
      setImageError(true);
      imgRef.current = null;
    };

    img.src = src;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [src, updateScale]);

  // Redraw and emit when rects or image changes
  useEffect(() => {
    if (!imgRef.current || !imageLoaded) return;
    redraw();
    emitRedactedImage();
  }, [rects, redraw, emitRedactedImage, imageLoaded]);

  // Redraw when previewMode changes
  useEffect(() => {
    if (!imgRef.current || !imageLoaded) return;
    redraw();
  }, [previewMode, redraw, imageLoaded]);

  // Observe resize for scale updates
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const ro = new ResizeObserver(updateScale);
    ro.observe(wrapper);
    return () => ro.disconnect();
  }, [updateScale]);

  // Sync with initialRects when they change
  useEffect(() => {
    setRects(initialRects);
  }, [initialRects]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const deleteButtons = useMemo(
    () =>
      rects.map((r, i) => ({
        key: r.id || i,
        left: (r.x + r.w) * scale - 12,
        top: r.y * scale - 12,
        index: i,
      })),
    [rects, scale]
  );

  if (imageError) {
    return <Container className={className}>{t('documents.failedToLoadImage')}</Container>;
  }

  return (
    <Container className={className}>
      <Wrapper ref={wrapperRef}>
        <Canvas
          ref={canvasRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={commitRect}
          onMouseLeave={commitRect}
        />
        {!previewMode &&
          deleteButtons.map(btn => (
            <DeleteButton
              key={btn.key}
              title="Remove redaction"
              data-index={btn.index}
              style={{ left: btn.left, top: btn.top }}
              onMouseDown={stopPropagation}
              onClick={handleDeleteClick}
            >
              &times;
            </DeleteButton>
          ))}
      </Wrapper>
    </Container>
  );
};

ImageRedactor.propTypes = {
  src: PropTypes.string.isRequired,
  initialRects: PropTypes.arrayOf(
    PropTypes.shape({
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired,
      w: PropTypes.number.isRequired,
      h: PropTypes.number.isRequired,
    })
  ),
  onChange: PropTypes.func,
  onRedactedImageChange: PropTypes.func,
  previewMode: PropTypes.bool,
  className: PropTypes.string,
};

ImageRedactor.defaultProps = {
  initialRects: [],
  onChange: undefined,
  onRedactedImageChange: undefined,
  previewMode: false,
  className: undefined,
};

export default ImageRedactor;
